import { darken, rgba, lighten, cssVar } from 'polished';
import { css } from 'styled-components';

const extractCSSVarName = (cssVar) => cssVar.substring(4, cssVar.length - 1);
const isUsingCssVar = (colorAlias) => colorAlias.startsWith('var(');
const getColorAliasValue = (colorAlias) => {
    if (isUsingCssVar(colorAlias)) {
        const varName = extractCSSVarName(colorAlias);
        return String(cssVar(varName, '#000000'));
    }
    return colorAlias;
};
const getDarkenColor = (darkenKey, alias) => (props) => {
    try {
        const darkenAmount = Number(props.theme.mode === 'dark'
            ? props.theme.alias.dark[darkenKey]
            : props.theme.alias.light[darkenKey]) / 100;
        const colorAlias = getColorAlias(alias, props.theme);
        if (typeof colorAlias !== 'string')
            throw new Error('Invalid alias');
        const colorValue = getColorAliasValue(colorAlias);
        return darken(darkenAmount, colorValue);
    }
    catch (error) {
        console.warn(error);
        return getColorAlias(alias, props.theme);
    }
};
const getLightenColor = (lightenKey, alias) => (props) => {
    try {
        const lightenAmount = Number(props.theme.mode === 'dark'
            ? props.theme.alias.dark[lightenKey]
            : props.theme.alias.light[lightenKey]) / 100;
        const colorAlias = getColorAlias(alias, props.theme);
        if (typeof colorAlias !== 'string')
            throw new Error('Invalid alias');
        const colorValue = getColorAliasValue(colorAlias);
        return lighten(lightenAmount, colorValue);
    }
    catch (error) {
        console.warn(error);
        return getColorAlias(alias, props.theme);
    }
};
const transparentizeAliasColor = (darkenKey, alias) => (props) => {
    try {
        const transparentAmount = typeof darkenKey === 'number'
            ? darkenKey
            : Number(props.theme.mode === 'dark'
                ? props.theme.alias.dark[darkenKey]
                : props.theme.alias.light[darkenKey]) / 100;
        const colorAlias = getColorAlias(alias, props.theme);
        if (typeof colorAlias !== 'string')
            throw new Error('Invalid alias');
        const colorValue = getColorAliasValue(colorAlias);
        return rgba(colorValue, transparentAmount);
    }
    catch (error) {
        console.warn(error);
        return getColorAlias(alias, props.theme);
    }
};
const getFontSize = (fontSize) => {
    return css `
    ${(props) => {
        const font = props.theme.font[fontSize];
        return `
        font-size: ${font.fontSize};
        line-height: ${font.lineHeight};
      `;
    }}
  `;
};
const getColorAlias = (alias, theme) => {
    if (theme) {
        return theme.mode === 'dark'
            ? theme.alias.dark[alias]
            : theme.alias.light[alias];
    }
    else {
        return (props) => {
            return props.theme.mode === 'dark'
                ? props.theme.alias.dark[alias]
                : props.theme.alias.light[alias];
        };
    }
};
const noop = () => { };
const slugify = (string) => string.toLowerCase().replace(/[^a-z0-9]/g, '-');
const unitJSX = null;
const getDefaultTransition = (element = 'all') => {
    return css `
    transition: ${({ theme }) => `${element} ${theme.motion.speed.defaultS} ${theme.motion.easing.easeDefault}`};
  `;
};
/**
 *
 * Use this function for getting spacing values from the theme.
 * @param values {SpacingKey}
 * 's00' => 0px;
 * 's01' => 4px;
 * 's02' => 8px;
 * 's03' => 12px;
 * 's04' => 16px;
 * 's05' => 24px;
 * 's06' => 32px;
 * 's07' => 40px;
 * 's08' => 56px;
 * 's09' => 64px;
 * 's10' => 80px;
 */
const getSpacing = (...values) => (props) => {
    if (values.length > 4)
        console.warn('You added to many values');
    const spacing = props.theme.spacing;
    return values.reduce((acc, curr) => `${acc} ${spacing[curr]}`, '');
};
/**
 * Use this curried function for appending a string to the data-testid or data-trackid
 * provided to the component. This makes it easier to add those values the wrapper
 * element and sub-elements that a complex component might have.
 * @param id: the data-testid or data-trackid provided to the component
 * @returns function: (addon: string): string
 */
const getTestOrTrackId = (id) => {
    return (addon) => id && addon ? `${id}-${addon}` : id ? id : undefined;
};

export { extractCSSVarName, getColorAlias, getColorAliasValue, getDarkenColor, getDefaultTransition, getFontSize, getLightenColor, getSpacing, getTestOrTrackId, isUsingCssVar, noop, slugify, transparentizeAliasColor, unitJSX };
